import React, {useState, useContext} from 'react';
import PropTypes from 'prop-types';
import validator from 'validator';
import {Anchor, Button, Modal, Dialogue, TextField, Text, addToast} from '@albert-io/atomic';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';

import PeopleContext from '../../people.context';
import {coteachersHelpArticleUrl} from '../coteachers.utils';

const AddCoteacher = () => {
  const [showAddCoteacherModal, setShowAddCoteacherModal] = useState(false);
  const toggleShowCoteacherModal = () => setShowAddCoteacherModal(!showAddCoteacherModal);

  return (
    <>
      <Button onClick={toggleShowCoteacherModal}>Add co-teacher</Button>
      {showAddCoteacherModal && <AddCoteacherModal handleClose={toggleShowCoteacherModal} />}
    </>
  );
};

export const AddCoteacherModal = ({handleClose}) => {
  const {classroom, queries} = useContext(PeopleContext);
  const [isRequestPending, setIsRequestPending] = useState(false);
  const [error, setError] = useState(null);
  async function handleAddCoteacher(e) {
    e.preventDefault();

    const email = e.target.elements.email.value;

    if (!email) {
      setError(`A valid email address is required. Please try again.`);
      return;
    }

    if (!validator.isEmail(email)) {
      setError(`${email} is not a valid email. Please try again.`);
      return;
    }

    try {
      setIsRequestPending(true);
      await genericMandarkRequest('patch', {
        resourcePath: [
          'classrooms_v1',
          classroom.getId(),
          `add_teacher?email=${encodeURIComponent(email.toLowerCase())}`
        ]
      });

      invalidatePartialInterest({
        resourcePath: queries.coteachers.done().resourcePath
      });

      handleClose();

      addToast({
        color: 'positive',
        title: 'Success',
        message: `${email} was added as a co-teacher!`
      });
    } catch (err) {
      setError(
        `${email} has not been approved. Contact your school admin to approve this user or contact success@albert.io for more help`
      );
    } finally {
      setIsRequestPending(false);
    }
  }
  return (
    <Modal ariaLabel='Add co-teacher' handleClose={handleClose}>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <Dialogue
            inModal
            className={modalContentStyle}
            handleClose={handleClose}
            title='Add co-teacher'
          >
            <form onSubmit={handleAddCoteacher} noValidate>
              <Dialogue.Body>
                <Text as='p' className='mb-x2'>
                  You must use an email address that has been pre-approved by your school.{' '}
                  <Anchor
                    href={coteachersHelpArticleUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    as='a'
                  >
                    Learn more about co-teachers.
                  </Anchor>
                </Text>
                <TextField
                  border
                  type='email'
                  label='Co-teacher email'
                  placeholder='coteacher@albert.edu'
                  name='email'
                  onChange={() => {
                    if (error) {
                      setError(null);
                    }
                  }}
                  error={!!error}
                  message={error}
                  data-testid='add-coteacher-email'
                />
              </Dialogue.Body>
              <Dialogue.BtnGroup>
                <CloseButtonWrapper>
                  <Button disabled={isRequestPending} type='button' color='secondary'>
                    Cancel
                  </Button>
                </CloseButtonWrapper>
                <Button disabled={isRequestPending} type='submit'>
                  Add
                </Button>
              </Dialogue.BtnGroup>
            </form>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

AddCoteacherModal.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default AddCoteacher;
